import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  Stack,
  CircularProgress,
  Snackbar,
  Modal,
  Fade
} from '@mui/material';
import { useAxios } from '../context/AxiosHookContext';

export default function QRConfirmation() {
  const { activityId } = useParams();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmModal, setConfirmModal] = useState({ open: false, message: '', success: false });
  const [activityTitle, setActivityTitle] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const axios = useAxios();

  const startCamera = async () => {
    try {
      setScanning(true);
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      setError('No se pudo acceder a la cámara. Verifique los permisos.');
      setScanning(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const captureAndScan = async () => {
    if (!videoRef.current || !canvasRef.current || loading) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR(imageData.data, imageData.width, imageData.height);

      if (code) {
        await confirmAttendance(code.data);
      }
    } catch (err) {
      console.error('Error scanning QR:', err);
    }
  };

  const confirmAttendance = async (recordId) => {
    try {
      setLoading(true);
      const response = await axios.put(`/activities/assistants/confirm/${recordId}`);
      
      if (response.data.success) {
        setConfirmModal({
          open: true,
          message: 'Asistencia confirmada exitosamente',
          success: true
        });
        
        // Cerrar modal automáticamente en 1 segundo
        setTimeout(() => {
          setConfirmModal(prev => ({ ...prev, open: false }));
        }, 1000);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setConfirmModal({
        open: true,
        message: err.response?.data?.message || 'Error al confirmar',
        success: false
      });
      
      // Cerrar modal automáticamente en 1 segundo
      setTimeout(() => {
        setConfirmModal(prev => ({ ...prev, open: false }));
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const getActivityTitle = async () => {
    try {
      const response = await axios.get(`/activities/activities/${activityId}`);
      setActivityTitle(response.data.actividad || 'Actividad');
    } catch (err) {
      setActivityTitle('Actividad');
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    document.head.appendChild(script);

    getActivityTitle();
    
    // Auto-iniciar cámara
    setTimeout(startCamera, 1000);

    return () => {
      stopCamera();
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (scanning && !loading) {
      interval = setInterval(captureAndScan, 300);
    }
    return () => clearInterval(interval);
  }, [scanning, loading]);

  return (
    <>
      <Helmet>
        <title>Escáner QR | ASISCORE</title>
      </Helmet>


        <Card sx={{ 
          width: '100%',
          p: 4, 
          borderRadius: 3, 
          textAlign: 'center',
          boxShadow: 31,
          
        }}>
          <Stack spacing={3} >
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                📱 Escáner QR
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary' }}>
                {activityTitle}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Escanee los códigos QR para confirmar asistencias
              </Typography>
            </Box>

            {/* Camera */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}>
              <Box sx={{ 
                position: 'relative', 
                borderRadius: 3, 
                overflow: 'hidden',
                width: 300,
                height: 250,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'grey.100'
              }}>
                {scanning ? (
                  <>
                    <video
                      ref={videoRef}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      playsInline
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    
                    {/* Scanning Frame */}
                    <Box sx={{
                      position: 'absolute',
                      top: '20%',
                      left: '20%',
                      right: '20%',
                      bottom: '20%',
                      border: '3px solid #00ff00',
                      borderRadius: 2,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -3,
                        left: -3,
                        right: -3,
                        height: 3,
                        bgcolor: '#00ff00',
                        animation: 'scan 2s linear infinite'
                      }
                    }} />
                  </>
                ) : (
                  <Typography variant="h6" color="text.secondary">
                    Iniciando cámara...
                  </Typography>
                )}
                
                {loading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CircularProgress sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
            </Box>



            {/* Controls */}
            {scanning && (
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  size="large"
                  onClick={stopCamera}
                  sx={{ minWidth: 150, py: 1.5 }}
                >
                  ⏹️ Detener
                </Button>
              </Stack>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Stack>
        </Card>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ minWidth: 300 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de Confirmación */}
      <Modal
        open={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Fade in={confirmModal.open}>
          <Box sx={{
            bgcolor: 'white',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            minWidth: 300,
            boxShadow: 24,
            border: confirmModal.success ? '3px solid #4caf50' : '3px solid #f44336'
          }}>
            <Box sx={{ fontSize: 60, mb: 2 }}>
              {confirmModal.success ? '✅' : '❌'}
            </Box>
            <Typography variant="h5" fontWeight={600} sx={{
              color: confirmModal.success ? 'success.main' : 'error.main',
              mb: 1
            }}>
              {confirmModal.success ? '¡Confirmado!' : '¡Error!'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {confirmModal.message}
            </Typography>
          </Box>
        </Fade>
      </Modal>

      <style jsx>{`
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </>
  );
}